import * as React from "react";
import { observer } from "mobx-react";
import * as T from "sb-resp-ui";
import { BaseBlockOptions } from "sb-resp-lib";

import { RacingRegionSwitcherRoot } from "../RacingRegionSwitcher";
import { RacingCurrentEventsRoot } from "../RacingCurrentEvents";
import { RacingFutureEventsRoot } from "../RacingFutureEvents";
import { RacingNextRacesRoot } from "../RacingNextRaces";

// imported only for typings
// these are passed to the component from index.ts
import { BaseDict } from "./dict";
import { RacingCombinedViewData } from "./data";

// external components
// import * as CMP from "./components";
// import * as SCMP from "../../components";

/*
    Setup testing tools for MobX
    uncoment the <DevTools /> at the end of the RacingCombinedView component
*/
// import DevTools from "mobx-react-devtools";
// import { setLogEnabled, setUpdatesEnabled, setGraphEnabled } from "mobx-react-devtools";
// if (process.env.NODE_ENV === "development") {
//     setLogEnabled(true);
//     setUpdatesEnabled(true);
//     setGraphEnabled(true);
// }

@observer
export class RacingCombinedView extends React.Component<{
    dataProvider: RacingCombinedViewData,
    dict: BaseDict
    options: BaseBlockOptions
    racingGameBlockNav: Function
}, {
    tabActive: string
    dataProvider: RacingCombinedViewData
}> {
    tabs!: T.Tabs | null
    nextRaces!: RacingNextRacesRoot
    internalBlocks!: {
        region: RacingRegionSwitcherRoot
        nextRaces: RacingNextRacesRoot
        current: RacingCurrentEventsRoot
        tomorrow: RacingCurrentEventsRoot
        future: RacingFutureEventsRoot
    }
    tabsInstances!: {
        current: RacingCurrentEventsRoot
        tomorrow: RacingCurrentEventsRoot
        future: RacingFutureEventsRoot
    }
    racingGameBlockNav!: Function
    cachedState: {
        branchId: any
        regionId: any
    }

    constructor(props) {
        super(props);
        this.state = {
            tabActive: "current",
            dataProvider: this.props.dataProvider,
        }
        this.cachedState = {
            branchId: this.state.dataProvider.racingStore.branchId,
            regionId: this.state.dataProvider.racingStore.regionId
        }
        // window["t"] = this;
    }

    /* istanbul ignore next */
    componentDidMount() {
        // setup instances of the blocks
        // activate/deactivate is managed elsewhere
        this.internalBlocks = {
            region: new RacingRegionSwitcherRoot("racing-region-switcher", { ...this.props.options }),
            nextRaces: new RacingNextRacesRoot("racing-next-races", { ...this.props.options }),
            current: new RacingCurrentEventsRoot("racing-current-events", { ...this.props.options }, this.props.racingGameBlockNav),
            tomorrow: new RacingCurrentEventsRoot("racing-tomorrow-events", { ...this.props.options }, this.props.racingGameBlockNav),
            future: new RacingFutureEventsRoot("racing-future-events", { ...this.props.options })
        }

        // link tabs instances
        this.tabsInstances = {
            current: this.internalBlocks.current,
            tomorrow: this.internalBlocks.tomorrow,
            future: this.internalBlocks.future
        };

        this.activateMain();
    }

    /* istanbul ignore next */
    activateMain() {
        // blocks active when the view first loads
        this.internalBlocks.region.activate();
        if (this.state.dataProvider.racingStore.isNextRacesAvailable) {
            this.internalBlocks.nextRaces.activate();
        } else {
            this.internalBlocks.nextRaces.deactivate();
        }

        this.internalBlocks.current.setParams(0);
        this.internalBlocks.current.activate();

        this.internalBlocks.tomorrow.setParams(1);
    }

    /* istanbul ignore next */
    deactivateAll() {
        Object.keys(this.internalBlocks).forEach(x => {
            typeof this.internalBlocks[x].deactivate === "function" && this.internalBlocks[x].deactivate();
        });
    }

    /* istanbul ignore next */
    componentWillUnmount() {
        this.deactivateAll();
    }

    /* istanbul ignore next */
    callTab(tab) {
        Object.keys(this.tabsInstances).forEach(x => this.tabsInstances[x][x === tab ? "activate" : "deactivate"]());
        this.setState({ tabActive: tab });
    }

    /* istanbul ignore next */
    renderTabTrigger(text: string, isDefault: boolean, link: string) {
        return (
            <div className="rj-generic-tabs-01-outer" onClick={() => this.callTab(link)}>
                <T.TabTrigger default={isDefault} link={link}>
                    <a className="rj-generic-tabs-01">{text}</a>
                </T.TabTrigger>
            </div>
        )
    }

    /* istanbul ignore next */
    renderTabBlock(id: string, link: string) {
        return (
            <T.TabBlock link={link}><div id={id}></div></T.TabBlock>
        )
    }

    /* istanbul ignore next */
    getSnapshotBeforeUpdate(prevProps, prevState) {
        if (
            this.cachedState.branchId !== this.state.dataProvider.racingStore.branchId ||
            this.cachedState.regionId !== this.state.dataProvider.racingStore.regionId
        ) {
            return {
                branchId: this.state.dataProvider.racingStore.branchId,
                regionId: this.state.dataProvider.racingStore.regionId
            };
        }
        /* tslint:disable:no-null-keyword */
        return null;
        /* tslint:enable:no-null-keyword */
    }

    /* istanbul ignore next */
    componentDidUpdate(revProps, prevState, snapshot) {
        if (snapshot !== null) {
            this.deactivateAll();
            this.tabs && this.tabs.resetTabs();
            this.cachedState = snapshot;
        }

        this.activateMain();
    }

    render() {
        return (
            <div data-combined-active-view={this.state.tabActive} data-combined-region={this.state.dataProvider.racingStore.regionId}>
                <div id="racing-region-switcher"></div>
                <div id="racing-next-races" data-next-races={this.state.dataProvider.racingStore.isNextRacesAvailable}></div>
                <T.Tabs ref={n => this.tabs = n}>
                    <div className="rj-generic-tabs-01-container">
                        <T.TabsTriggers>
                            {this.renderTabTrigger(this.props.dict.TodayEvents, true, "current")}

                            {this.state.dataProvider.racingStore.isTomorrowEventsPresent &&
                                this.renderTabTrigger(this.props.dict.TomorrowEvents, false, "tomorrow")}

                            {this.state.dataProvider.racingStore.isFutureEventsPresent &&
                                this.renderTabTrigger(this.props.dict.AntepostEvents, false, "future")}
                        </T.TabsTriggers>
                    </div>
                    <div className="racing-combined-view__tabs-blocks">
                        <T.TabsBlocks>
                            {this.renderTabBlock("racing-current-events", "current")}
                            {this.renderTabBlock("racing-tomorrow-events", "tomorrow")}
                            {this.renderTabBlock("racing-future-events", "future")}
                        </T.TabsBlocks>
                    </div>
                </T.Tabs>

                {/* {process.env.NODE_ENV === "development" && (
                    <DevTools />
                )} */}
            </div>
        )
    }
}